import React, { useEffect, useState } from 'react';
import { Clock, CheckCircle2, Banknote, Wallet, Users, Ticket, RotateCcw } from 'lucide-react';
import api from '@/lib/api';
import { formatTL, monthLabel, formatDate } from '@/lib/format';
import { Card, StatusBadge, SectionTitle, SectionLabel, StatHero, ListItem, Empty } from '@/components/shared';

const TYPE_LABELS = { commission: 'Satış komisyonu', referral: 'Davet primi', clubcard: 'Kulüp kartı bonusu', adjustment: 'İade düzeltmesi' };

export default function Earnings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/architect/earnings').then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-ink/40 text-sm">Yükleniyor...</div>;
  if (!data) return <Empty text="Veri yok" />;

  return (
    <div className="px-5 py-6 fade-up" data-testid="earnings-page">
      <h1 className="font-serif text-2xl text-ink mb-5">Hakediş</h1>

      <Card className="p-6 mb-4">
        <StatHero
          label="Ödemeye Hazır (Onaylandı)"
          value={formatTL(data.readyToPay)}
          valueClass="text-pos"
          testid="earnings-ready"
        />
        <p className="text-xs text-ink-soft mt-3 flex items-center gap-1.5">
          <Banknote strokeWidth={1.5} className="w-3.5 h-3.5" /> Onaylanan hak edişler her ay düzenli ödenir.
        </p>
      </Card>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <Card className="p-4">
          <span className="w-[34px] h-[34px] rounded-full border border-line flex items-center justify-center mb-3">
            <Clock strokeWidth={1.5} className="w-4 h-4 text-gold" />
          </span>
          <div className="font-serif text-xl text-ink tabular" data-testid="earnings-pending">{formatTL(data.pendingApproval)}</div>
          <div className="text-ink-soft text-xs mt-0.5">Onay bekleyen</div>
        </Card>
        <Card className="p-4">
          <span className="w-[34px] h-[34px] rounded-full border border-line flex items-center justify-center mb-3">
            <CheckCircle2 strokeWidth={1.5} className="w-4 h-4 text-pos" />
          </span>
          <div className="font-serif text-xl text-ink tabular" data-testid="earnings-paid-ytd">{formatTL(data.paidYearToDate)}</div>
          <div className="text-ink-soft text-xs mt-0.5">Bu yıl ödenen</div>
        </Card>
      </div>

      {/* Earnings breakdown */}
      <SectionLabel>Kazanç Dağılımı</SectionLabel>
      <div className="space-y-2 mb-6" data-testid="earnings-breakdown">
        <ListItem icon={Wallet} iconColor="rgb(var(--ink-soft))" title="Satış komisyonu" amount={formatTL(data.commissionTotal)} sub="" />
        <ListItem icon={Users} iconColor="rgb(var(--gold))" title="Davet primi" amount={formatTL(data.referralTotal)} positive />
        <ListItem icon={Ticket} iconColor="rgb(var(--gold))" title="Kulüp kartı bonusu" amount={formatTL(data.clubCardTotal)} positive />
      </div>

      {/* Recent earnings with statuses */}
      {data.entries && data.entries.length > 0 && (
        <>
          <SectionLabel>Son Hareketler</SectionLabel>
          <div className="space-y-2 mb-6" data-testid="earnings-entries">
            {data.entries.map((e) => (
              <Card key={e.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-[34px] h-[34px] rounded-full border border-line flex items-center justify-center shrink-0">
                    {e.type === 'adjustment'
                      ? <RotateCcw strokeWidth={1.5} className="w-4 h-4 text-redx" />
                      : <Wallet strokeWidth={1.5} className="w-4 h-4 text-ink-soft" />}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-ink leading-tight">{TYPE_LABELS[e.type] || e.type}</p>
                    <p className="text-[11.5px] text-ink-soft mt-0.5">
                      {e.status === 'pending' && e.holdUntil ? `Onay tarihi: ${formatDate(e.holdUntil)}` :
                        e.status === 'paid' && e.paidPeriod ? `${monthLabel(e.paidPeriod)} döneminde ödendi` :
                        formatDate(e.date)}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className={`font-serif text-base tabular ${e.amount < 0 ? 'text-redx' : 'text-ink'}`}>{formatTL(e.amount)}</p>
                  <div className="mt-1"><StatusBadge status={e.status} /></div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      <SectionTitle>Ödeme geçmişi</SectionTitle>
      {data.history.length === 0 ? (
        <Empty text="Ödeme kaydı yok" />
      ) : (
        <div className="space-y-2" data-testid="earnings-history">
          {data.history.map((p) => (
            <Card key={p.id} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-[34px] h-[34px] rounded-full border border-line flex items-center justify-center shrink-0">
                  <Banknote strokeWidth={1.5} className="w-4 h-4 text-ink-soft" />
                </span>
                <div className="min-w-0">
                  <p className="text-[13.5px] font-semibold text-ink leading-tight">{monthLabel(p.period)} döneminde ödendi</p>
                  {(p.referralAmount > 0 || p.clubCardAmount > 0 || (p.adjustmentAmount && p.adjustmentAmount !== 0)) ? (
                    <p className="text-[11.5px] text-ink-soft mt-0.5 truncate">
                      Kom. {formatTL(p.commissionAmount)}
                      {p.referralAmount > 0 && <span className="text-gold"> · Davet {formatTL(p.referralAmount)}</span>}
                      {p.clubCardAmount > 0 && <span className="text-gold"> · Kulüp {formatTL(p.clubCardAmount)}</span>}
                      {p.adjustmentAmount && p.adjustmentAmount !== 0 ? <span className="text-redx"> · Düzeltme {formatTL(p.adjustmentAmount)}</span> : null}
                    </p>
                  ) : (
                    <p className="text-[11.5px] text-ink-soft mt-0.5">{p.paidAt ? `Ödeme: ${formatDate(p.paidAt)}` : ''}</p>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="font-serif text-lg text-ink tabular">{formatTL(p.amount)}</p>
                <div className="mt-1"><StatusBadge status={p.status} /></div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
